/**
 * RankingsEmbed - Embeddable leaderboard widget
 *
 * Minimal UI for embedding on external sites.
 * Query params:
 * - strategy: SAT_MATH, SAT_MATH_MINI, SAT_READING_WRITING, SAT_READING_WRITING_MINI (required)
 * - theme: light, dark (default: light)
 * - limit: 1-20 (default: 10)
 *
 * Example: /rankings/embed?strategy=SAT_MATH&theme=dark&limit=5
 */
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import rankingsService from '../services/rankingsService'
import { Trophy, ExternalLink } from 'lucide-react'
import { createLogger } from '../utils/logger'

const logger = createLogger('RankingsEmbed')

const RankingsEmbed = () => {
  const [searchParams] = useSearchParams()
  const [leaderboard, setLeaderboard] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Strategy display info
  const strategyInfo = {
    SAT_MATH: { name: 'SAT Math', icon: '🧮' },
    SAT_MATH_MINI: { name: 'SAT Math Mini', icon: '⚡' },
    SAT_READING_WRITING: { name: 'SAT Reading & Writing', icon: '📖' },
    SAT_READING_WRITING_MINI: { name: 'SAT Reading & Writing Mini', icon: '✏️' }
  }

  // Valid strategy types for validation
  const validStrategies = Object.keys(strategyInfo)

  // Parse query params - validate strategy and fallback to SAT_MATH if invalid
  const requestedStrategy = searchParams.get('strategy') || 'SAT_MATH'
  const strategy = validStrategies.includes(requestedStrategy) ? requestedStrategy : 'SAT_MATH'
  const theme = searchParams.get('theme') || 'light'
  const limit = Math.min(Math.max(parseInt(searchParams.get('limit') || '10', 10), 1), 20)

  const currentStrategy = strategyInfo[strategy]

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await rankingsService.getLeaderboard(strategy, {
          period: 'OVERALL_BEST',
          limit
        })
        setLeaderboard(data.entries || [])
      } catch (err) {
        logger.error('Error fetching leaderboard:', err)
        setError('Failed to load leaderboard')
      } finally {
        setLoading(false)
      }
    }

    fetchLeaderboard()
  }, [strategy, limit])

  // Apply theme
  const isDark = theme === 'dark'
  const bgClass = isDark ? 'bg-gray-900' : 'bg-white'
  const textClass = isDark ? 'text-gray-100' : 'text-gray-900'
  const mutedClass = isDark ? 'text-gray-400' : 'text-gray-600'
  const borderClass = isDark ? 'border-gray-700' : 'border-gray-200'
  const hoverClass = isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'

  if (loading) {
    return (
      <div className={`${bgClass} ${textClass} p-4 min-h-[200px] flex items-center justify-center`}>
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <span className={mutedClass}>Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`${bgClass} ${textClass} p-4 min-h-[200px] flex items-center justify-center`}>
        <p className={mutedClass}>{error}</p>
      </div>
    )
  }

  const appUrl = window.location.origin

  return (
    <div className={`${bgClass} ${textClass} font-sans`}>
      {/* Header */}
      <div className={`p-3 border-b ${borderClass} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <span className="font-semibold">
            {currentStrategy.icon} {currentStrategy.name}
          </span>
        </div>
        <a
          href={`${appUrl}/rankings`}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-xs ${mutedClass} hover:text-primary-500 flex items-center gap-1`}
        >
          View All
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Leaderboard */}
      {leaderboard.length === 0 ? (
        <div className="p-8 text-center">
          <Trophy className={`w-8 h-8 mx-auto mb-2 ${mutedClass}`} />
          <p className={mutedClass}>No rankings yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {leaderboard.map((entry, index) => {
            const rank = index + 1
            const rankDisplay = getRankDisplay(rank)

            return (
              <div
                key={entry.id || index}
                className={`flex items-center gap-3 p-3 ${hoverClass} transition-colors`}
              >
                {/* Rank */}
                <div className="w-8 text-center">
                  {rankDisplay.isMedal ? (
                    <span className="text-lg">{rankDisplay.display}</span>
                  ) : (
                    <span className={`font-semibold ${mutedClass}`}>{rankDisplay.display}</span>
                  )}
                </div>

                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                  {(entry.user?.publicName || 'A')[0].toUpperCase()}
                </div>

                {/* Name and Score */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {entry.user?.publicName || 'Anonymous'}
                  </div>
                </div>

                {/* Score */}
                <div className="text-right">
                  <span className="font-semibold">
                    {rankingsService.formatScore({ ...entry, strategyType: strategy })}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Footer CTA */}
      <div className={`p-3 border-t ${borderClass} text-center`}>
        <a
          href={`${appUrl}/signup`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
        >
          Join and see your ranking →
        </a>
      </div>
    </div>
  )
}

/**
 * Get rank display with medals for top 3
 */
const getRankDisplay = (rank) => {
  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' }
  if (medals[rank]) {
    return { display: medals[rank], isMedal: true }
  }
  return { display: `#${rank}`, isMedal: false }
}

export default RankingsEmbed
